import React, { useState } from 'react'
import KaTeXRenderer from '../components/common/KaTeXRenderer'
import QuestionRenderer from '../components/quiz/QuestionRenderer'
import { BankQuestion } from '../services/bankQuestionService_moi'

const KaTeXTest: React.FC = () => {
  const [testMode, setTestMode] = useState<'basic' | 'question'>('basic')

  // Sample KaTeX expressions for testing
  const basicTests = [
    {
      title: 'Inline Math',
      content: 'Phương trình bậc hai: $ax^2 + bx + c = 0$ có nghiệm khi $\\Delta \\geq 0$'
    },
    {
      title: 'Display Math',
      content: 'Công thức nghiệm: $$x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}$$'
    },
    {
      title: 'Physics Formula',
      content: 'Định luật Newton thứ hai: $$F = ma$$ và động năng: $$E_k = \\frac{1}{2}mv^2$$'
    },
    {
      title: 'Complex Expression',
      content: 'Phương trình sóng: $$y = A\\sin(\\omega t - kx + \\phi)$$ với tần số góc $\\omega = 2\\pi f$'
    },
    {
      title: 'Fractions and Roots',
      content: 'Vận tốc: $v = \\frac{s}{t}$ và gia tốc: $a = \\frac{\\Delta v}{\\Delta t} = \\frac{v_2 - v_1}{t_2 - t_1}$'
    },
    {
      title: 'Greek Letters',
      content: 'Các ký hiệu: $\\alpha, \\beta, \\gamma, \\delta, \\epsilon, \\theta, \\lambda, \\mu, \\pi, \\sigma, \\omega, \\Omega$'
    },
    {
      title: 'Complex Multi-line Formula with align',
      content: 'Từ đồ thị ta thấy: $\\left\\{ \\begin{align} & {{d}_{QM}}=3 \\\\ & \\frac{λ }{2}=3 \\\\ \\end{align} \\right.⇒ \\left\\{ \\begin{align} & \\Delta \\varphi =\\frac{2π {{d}_{QM}}}{λ }=\\frac{2π.3}{6}=π \\\\ & λ =6 \\\\ \\end{align} \\right.$'
    },
    {
      title: 'System of Equations',
      content: 'Giải hệ phương trình: $\\left\\{ \\begin{align} & x + y = 5 \\\\ & 2x - y = 1 \\\\ \\end{align} \\right. ⇒ \\left\\{ \\begin{align} & x = 2 \\\\ & y = 3 \\\\ \\end{align} \\right.$'
    },
    {
      title: 'Multi-line Derivation',
      content: 'Biến đổi: $\\begin{align} F &= ma \\\\ &= m\\frac{\\Delta v}{\\Delta t} \\\\ &= \\frac{\\Delta p}{\\Delta t} \\end{align}$'
    }
  ]

  // Sample question with KaTeX
  const sampleQuestion: BankQuestion = {
    id: 'test-1',
    type: 'trac_nghiem_nhieu_lua_chon',
    cau_hoi: 'Một vật dao động điều hòa với phương trình: $x = 4\\cos(2\\pi t + \\frac{\\pi}{3})$ cm. Biên độ dao động là:',
    lua_chon: [
      '$A = 4$ cm',
      '$A = 2\\pi$ cm', 
      '$A = \\frac{\\pi}{3}$ cm',
      '$A = 2$ cm'
    ],
    dap_an_dung: 'A',
    loi_giai: 'Từ phương trình dao động điều hòa tổng quát: $$x = A\\cos(\\omega t + \\phi)$$ So sánh với phương trình đã cho: $$x = 4\\cos(2\\pi t + \\frac{\\pi}{3})$$ Ta có: biên độ $A = 4$ cm, tần số góc $\\omega = 2\\pi$ rad/s, pha ban đầu $\\phi = \\frac{\\pi}{3}$ rad.',
    goi_y: [
      'Phương trình dao động điều hòa có dạng: $x = A\\cos(\\omega t + \\phi)$',
      'Biên độ A là hệ số trước hàm cos'
    ],
    muc_do: 'M1',
    diem: 1
  }

  const [selectedAnswer, setSelectedAnswer] = useState<string>('')
  const [showSolution, setShowSolution] = useState(false)

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <h1 className="text-3xl font-bold text-center mb-8">
          Test KaTeX Rendering
        </h1>

        {/* Mode Selector */}
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-lg p-1 shadow-sm border border-gray-200">
            <button
              onClick={() => setTestMode('basic')}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                testMode === 'basic'
                  ? 'btn-primary'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Basic KaTeX
            </button>
            <button
              onClick={() => setTestMode('question')}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                testMode === 'question'
                  ? 'btn-primary'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Question Renderer
            </button>
          </div>
        </div>

        {testMode === 'basic' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">Basic KaTeX Tests</h2>
            
            {basicTests.map((test, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">{test.title}</h3>
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <code className="text-sm text-gray-600">{test.content}</code>
                </div>
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Rendered:</h4>
                  <KaTeXRenderer 
                    content={test.content}
                    className="text-gray-900"
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {testMode === 'question' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">Question Renderer Test</h2>
            
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 mb-4">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setSelectedAnswer('')}
                  className="btn-secondary px-4 py-2 rounded-lg"
                >
                  Reset
                </button>
                <button
                  onClick={() => setShowSolution(!showSolution)}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    showSolution
                      ? 'btn-soft'
                      : 'btn-primary'
                  }`}
                >
                  {showSolution ? 'Ẩn lời giải' : 'Hiện lời giải'}
                </button>
                {selectedAnswer && (
                  <span className="text-sm text-gray-600">
                    Đã chọn: <strong>{selectedAnswer}</strong>
                  </span>
                )}
              </div>
            </div>

            <QuestionRenderer
              question={sampleQuestion}
              questionNumber={1}
              selectedAnswer={selectedAnswer}
              onAnswerSelect={setSelectedAnswer}
              showSolution={showSolution}
            />
          </div>
        )}

        {/* Navigation */}
        <div className="text-center mt-8">
          <a
            href="/"
            className="btn-primary inline-flex items-center px-6 py-3 rounded-lg"
          >
            ← Về trang chủ
          </a>
        </div>
      </div>
    </div>
  )
}

export default KaTeXTest